import React, { useEffect, useState } from "react";
import { Tooltip } from "antd";

export const MONO_FONT =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

export const EmptyValue = () => <span style={{ color: "#9CA3AF" }}>—</span>;

export const EllipsisCell = ({
  value,
  mono = false,
  strong = false,
}: {
  value?: string;
  mono?: boolean;
  strong?: boolean;
}) => {
  if (!value) return <EmptyValue />;

  return (
    <Tooltip title={value} placement="topLeft">
      <span
        className={`block truncate text-sm text-dark-grey ${
          strong ? "font-semibold" : ""
        }`}
        style={{
          fontFamily: mono ? MONO_FONT : undefined,
          fontSize: mono ? 12 : undefined,
          maxWidth: "100%",
          lineHeight: 1.4,
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
};

export const MonoText = ({
  value,
  size = 12,
}: {
  value?: string;
  size?: number;
}) => {
  if (!value) return <EmptyValue />;

  return (
    <span
      className="text-dark-grey"
      style={{ fontFamily: MONO_FONT, fontSize: size, wordBreak: "break-all" }}
    >
      {value}
    </span>
  );
};

/**
 * A type's key set. 1-based and unique per notification type. `subtle` is for
 * types that own a single key set, where the number carries no choice.
 */
export const VariantBadge = ({
  variant,
  subtle = false,
}: {
  variant: number;
  subtle?: boolean;
}) => (
  <span
    className="inline-flex items-center rounded-md text-xs font-semibold whitespace-nowrap"
    style={{
      padding: "3px 8px",
      background: subtle ? "#EEF0F6" : "#2d3ac7",
      color: subtle ? "#6b7280" : "#fff",
      fontFamily: subtle ? MONO_FONT : undefined,
      letterSpacing: "0.01em",
    }}
  >
    {subtle ? `v${variant}` : `Variant ${variant}`}
  </span>
);

/**
 * Notification image thumbnail. Renders nothing unless the value is an http(s)
 * URL that actually loads — a dead or non-URL value would otherwise paint the
 * browser's broken-image glyph.
 */
export const PreviewImage = ({
  src,
  size = 44,
}: {
  src?: string;
  size?: number;
}) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || !/^https?:\/\//i.test(src) || failed) return null;

  return (
    <img
      src={src}
      alt=""
      onError={() => setFailed(true)}
      className="flex-shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        objectFit: "cover",
        border: "1px solid #EEF0F4",
      }}
    />
  );
};

export const CountBadge = ({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "accent" | "error" | "success";
}) => {
  const palette = {
    neutral: { background: "#EEF0F6", color: "#3F4654" },
    accent: { background: "#EDE9FE", color: "#2d3ac7" },
    error: { background: "#FDE8E8", color: "#B42318" },
    success: { background: "#E6F6E8", color: "#04AA0A" },
  }[tone];

  return (
    <span
      className="inline-flex items-center rounded-md text-xs font-semibold whitespace-nowrap"
      style={{ padding: "3px 8px", ...palette }}
    >
      {children}
    </span>
  );
};

/** Shared antd table chrome, scoped to a wrapper class per table. */
export const NotificationTableStyles = ({ scope }: { scope: string }) => (
  <style>{`
    .${scope} .ant-table {
      border: 1px solid #E6E8EF;
      border-radius: 10px;
      overflow: hidden;
    }
    .${scope} .ant-table-thead > tr > th {
      background: #F5F6FA !important;
      color: #3F4654;
      font-weight: 700;
      font-size: 13px;
      padding: 12px 16px !important;
      border-bottom: 1px solid #E6E8EF !important;
      white-space: nowrap;
    }
    .${scope} .ant-table-tbody > tr > td {
      padding: 10px 16px !important;
      border-bottom: 1px solid #EEF0F4 !important;
      vertical-align: middle;
      background: #fff !important;
    }
    /* antd's hidden column-measure row must not inherit cell padding. */
    .${scope} .ant-table-measure-row > td {
      padding: 0 !important;
      height: 0 !important;
      border: none !important;
    }
    .${scope} .ant-table-tbody > tr:hover > td {
      background: #F8F9FC !important;
    }
    .${scope} .ant-table-cell-fix-right {
      background: #fff !important;
      box-shadow: -4px 0 8px rgba(32, 34, 36, 0.04);
    }
    .${scope} .ant-table-tbody > tr:hover .ant-table-cell-fix-right {
      background: #F8F9FC !important;
    }
    .${scope} .ant-table-tbody > tr.notification-type-row > td {
      background: #FBFBFE !important;
      font-weight: 600;
    }
    .${scope} .ant-table-tbody > tr.notification-type-row:hover > td {
      background: #F5F6FA !important;
    }
  `}</style>
);
